'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';

const STORAGE_KEY = 'molwe_cookie_popup_dismissed_at';
const REAPPEAR_AFTER_DAYS = 14;

function nowMs() {
  return Date.now();
}

function daysToMs(days: number) {
  return days * 24 * 60 * 60 * 1000;
}

function shouldShowFromStorage(): boolean {
  try {
    const raw = globalThis?.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return true;

    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return true;

    return nowMs() - dismissedAt >= daysToMs(REAPPEAR_AFTER_DAYS);
  } catch {
    // If storage is unavailable, prefer showing so user can still close it for this session.
    return true;
  }
}

function writeDismissedAt() {
  try {
    globalThis?.localStorage?.setItem(STORAGE_KEY, String(nowMs()));
  } catch {
    // ignore
  }
}

export default function CookiePopup() {
  const [open, setOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setOpen(shouldShowFromStorage());
  }, []);

  const close = () => {
    writeDismissedAt();
    setOpen(false);
  };

  const collapsedText = useMemo(
    () => <>Оставаясь на сайте, вы соглашаетесь на их использование</>,
    [],
  );

  const expandedText = useMemo(
    () => (
      <>
        Мы используем наши собственные и сторонние файлы cookie для улучшения вашего опыта и наших
        услуг, анализируя, как люди используют наш сайт. Если вы продолжаете использовать веб-сайт,
        данные файлов cookie обрабатываются автоматически в соответствии с{' '}
        <Link
          className="underline underline-offset-2 hover:no-underline text-[#505CFF]"
          href="/terms"
        >
          Условиями использования сайта
        </Link>
        . Вы можете заблокировать их по инструкции, указанной в п. 13 Условий.
      </>
    ),
    [],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Any close should persist the dismissal (X/overlay/Esc)
        if (!next) close();
        else setOpen(true);
      }}
      modal={false}
    >
      <DialogContent
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        style={{
          position: 'fixed',
          right: 12,
          bottom: 12,
          left: 'auto',
          top: 'auto',
          transform: 'none',
          width: '270px',
          padding: '25px 14px 14px 14px',
          transition: 'none',
          animation: 'none',
        }}
        className="rounded-[10px] bg-white [&+*]:hidden !gap-0 w-[270px] block !duration-0 [&[data-state=open]]:!animate-none [&[data-state=closed]]:!animate-none"
        hideCloseButton
      >
        <DialogClose asChild>
          <button
            type="button"
            style={{
              position: 'absolute',
              right: '5px',
              top: '5px',
            }}
            className="text-[#BEBEBE] flex items-center justify-center rounded-full hover:text-black"
            onClick={close}
          >
            <X className="w-4 h-4" />
          </button>
        </DialogClose>
        <div className="mb-2">
          <DialogTitle
            className={`text-[14px] font-semibold text-[#1B1D22] ${!isExpanded ? 'text-center' : ''}`}
          >
            Мы используем Cookies-файлы
          </DialogTitle>
        </div>

        <p className={`text-[12px] text-[#898989] ${!isExpanded ? 'text-center' : ''}`}>
          {isExpanded ? expandedText : collapsedText}
        </p>

        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="py-[12px] text-[#505CFF] text-[12px] font-medium underline underline-offset-2 hover:no-underline"
        >
          {isExpanded ? 'Свернуть' : 'Подробнее'}
        </button>

        <div className="flex">
          <button
            type="button"
            className="bg-[#121212] text-[14px] text-white flex justify-center items-center rounded-[4px]"
            style={{
              backgroundColor: '#121212',
              height: '30px',
              padding: '0 13px',
            }}
            onClick={close}
          >
            Понятно
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
